import React, { useState, useEffect } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { usePostMessage } from '../PostMessageContext';
import SidebarNav from './SidebarNav';

function TopBar({ onToggleSidebar }) {
  const { user, brand } = useAuth();

  const getPlanInfo = () => {
    const planId = brand?.plan_id || brand?.brand_plan_id || 2;
    const plans = {
      1:   { name: 'Basic',        icon: 'fas fa-seedling',  color: '#64748B', bg: '#F1F5F9', border: '#CBD5E1' },
      7:   { name: 'Starter',      icon: 'fas fa-rocket',    color: '#0369A1', bg: '#E0F2FE', border: '#7DD3FC' },
      2:   { name: 'Professional', icon: 'fas fa-trophy',    color: '#B45309', bg: '#FFF8E1', border: '#F59E0B' },
      3:   { name: 'Agency',       icon: 'fas fa-building',  color: '#7C3AED', bg: '#F3E8FF', border: '#C084FC' },
    };
    return plans[planId] || plans[String(planId)] || null;
  };

  const plan = getPlanInfo();

  return (
    <nav
      className="flex items-center justify-between bg-white px-4 shrink-0"
      style={{
        height: '64px',
        borderBottom: '1px solid #DDE7F1',
        position: 'sticky',
        top: 0,
        zIndex: 10,
      }}
      aria-label="top navigation bar"
    >
      {/* Sidebar toggle - mobile only */}
      <button
        type="button"
        onClick={onToggleSidebar}
        data-testid="mobile-nav-open-button"
        className="lg:hidden p-2 rounded-md hover:bg-slate-100 transition-colors"
        style={{ background: 'none', border: 'none', cursor: 'pointer' }}
        aria-label="Toggle sidebar"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 16 16" fill="currentColor" style={{ width: '20px', height: '20px' }}>
          <path d="M0 3a2 2 0 0 1 2-2h12a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H2a2 2 0 0 1-2-2zm5-1v12h9a1 1 0 0 0 1-1V3a1 1 0 0 0-1-1zM4 2H2a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h2z" />
        </svg>
      </button>

      {/* Right side: plan + user profile */}
      <div className="ml-auto flex items-center gap-4">
        {plan && (
          <div className="hidden lg:flex items-center gap-2" data-testid="topbar-plan-badge">
            <span style={{ fontSize: '13px', color: '#94A3B8', fontWeight: '500' }}>Current plan:</span>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px',
              background: plan.bg, border: `1px solid ${plan.border}`, borderRadius: '6px',
              padding: '4px 10px', fontSize: '12px', fontWeight: '600', color: plan.color,
            }}>
              <i className={plan.icon} style={{ fontSize: '11px' }}></i>
              {plan.name}
            </span>
          </div>
        )}
        <Link
          to="/account"
          data-testid="user-profile-dropdown-trigger"
          className="flex items-center gap-2 p-2 rounded-lg hover:bg-slate-50 transition-colors group"
          style={{ textDecoration: 'none' }}
        >
          <div
            className="flex justify-center items-end bg-slate-200 rounded-full overflow-hidden group-hover:bg-blue-100 transition-colors"
            style={{ width: '32px', height: '32px' }}
          >
            <i className="fas fa-user text-slate-400 group-hover:text-blue-400 transition-colors" style={{ fontSize: '22px' }}></i>
          </div>
          <div className="hidden lg:flex flex-col">
            <span style={{ fontSize: '14px', fontWeight: '600', color: '#334155' }}>
              {user?.name || 'My Account'}
            </span>
          </div>
        </Link>
      </div>
    </nav>
  );
}

export default function ProtectedLayout({ children }) {
  const { user, isIframe, iframePending } = useAuth();
  const { showMenu } = usePostMessage();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // When toggle_menu opens the menu, also pop open the mobile sidebar
  useEffect(() => {
    if (showMenu) setSidebarOpen(true);
  }, [showMenu]);

  if (iframePending) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F5F8FC', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ color: '#94a3b8', fontSize: '14px' }}>Loading...</div>
      </div>
    );
  }

  if (!user && !isIframe) return <Navigate to="/login" replace />;

  if (isIframe && !showMenu) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#F5F8FC' }}>
        <main id="main-content" style={{ height: '100vh', overflowY: 'auto' }}>
          {children}
        </main>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'row', minHeight: '100vh', position: 'relative' }}>

      {/* Mobile overlay */}
      {sidebarOpen && (
        <div
          onClick={() => setSidebarOpen(false)}
          style={{
            position: 'fixed', inset: 0, zIndex: 40,
            background: 'rgba(12, 10, 9, 0.15)',
            backdropFilter: 'blur(2px)',
          }}
          className="lg:hidden"
        />
      )}

      {/* Sidebar — fixed on mobile (slides in/out), part of flow on desktop */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          height: '100vh',
          width: '256px',
          zIndex: 50,
          transform: sidebarOpen ? 'translateX(0)' : 'translateX(-256px)',
          transition: 'transform 300ms ease',
        }}
        className="lg:relative lg:!transform-none lg:!translate-x-0 lg:shrink-0"
      >
        <SidebarNav setSidebarOpen={setSidebarOpen} />
      </div>

      {/* Main column: TopBar + Page content */}
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: '100vh',
          overflow: 'hidden',
          marginLeft: '0',
        }}
        className="lg:ml-0"
      >
        <TopBar onToggleSidebar={() => setSidebarOpen(s => !s)} />

        {/* Page content */}
        <main
          id="main-content"
          style={{
            flex: 1,
            backgroundColor: '#F5F8FC',
            overflowY: 'auto',
          }}
        >
          {children}
        </main>
      </div>
    </div>
  );
}
